import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Button, Icon, message, Pagination, Radio, Select, Spin, Tooltip } from "antd";
import axios from "axios";
import CommonHeader from "../../CommonHeader";
import CustomHeader from "../../../../CommonHeader/CustomHeader";
import CustomAgGridTable from "../../../../Common-Table/CustomAgGridTable";
import {
  EA,
  FOLLOW_UP_SPECIALIST,
  FOLLOWUP,
  LEAD,
  OPPORTUNITY,
  PROSPECT,
  REVIEW,
  REVIEW_SCHEDULER,
  REVIEWADMIN,
  TYPE_TO_FILTER_BY_NAME,
} from "../../../../utils/Constants";
import {
  GET_ACTIVE_FS,
  GET_ACTIVE_RS,
  GET_FS_ALL_AGENCIES,
  RS_ALL_AGENCIES_API_URL,
  SCHEDULER_DAILY_AGENCY_TOGGLE_ASSIGNMENT,
  WEEKLY_DASH_SCHEDULER_BOARD_GRID,
  WEEKLY_DASH_SCHEDULER_BOARD_STATS,
  WEEKLY_DASH_SCHEDULER_DAY_PRIORITY,
  WEEKLY_DASH_SCHEDULER_WEEK_VIEW,
} from "../../../../utils/Api_enums";
import arrowIcon from "../../../../assets/icons/svg/arrow-icon.svg";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../../../Common-Table/CustomAgGridTable.css";
import "./styles/style.css";
import moment from "moment";
import F from "../../../../Functions";
import { Translate } from "react-translated";
import { useBoundStore } from "../../../../store/useBoundStore";

const { Option } = Select;

const PAGE_SIZE = 10;

const PACE_OPTIONS = [
  { id: "On Track", name: "On Track" },
  { id: "Behind", name: "Behind" },
];

/** Backend expects "All Carriers"; UI carrier list uses "All Carrier". */
const toApiCarrier = (carrier) =>
  !carrier || carrier === "All Carrier" ? "All Carriers" : carrier;

const formatStatValue = (value) => {
  if (value == null || value === "") return "0";
  return String(value);
};

const formatWeekViewTimestamp = (dateValue) => {
  if (!dateValue) return "—";
  const parsed = moment(dateValue);
  return parsed.isValid() ? parsed.format("MM-DD-YYYY hh:mm A") : "—";
};

const formatDayHeading = (day = {}) => {
  const parsed = moment(day.date);
  if (!parsed.isValid()) {
    return day.day_name || "—";
  }
  return `${day.day_name || parsed.format("dddd")}, ${parsed.format("MMM DD, YYYY")}`;
};

const formatConversionRate = (value) => {
  if (value == null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return `${value}`;
  return `${numeric.toFixed(1)}%`;
};

const isEnabledToggleValue = (value) =>
  value === true ||
  value === 1 ||
  String(value).toLowerCase() === "yes" ||
  String(value).toLowerCase() === "true";

const getWeekIdentity = (week = {}, index = 0) =>
  String(
    week.week_number != null
      ? `week-${week.week_number}`
      : week.label
        ? `label-${week.label}`
        : `idx-${index}`
  );

const calcWeekDetailHeight = (weeks = [], collapsedWeeks = {}) => {
  if (!weeks.length) return 420;
  const openWeeks = weeks.filter(
    (week, index) => !collapsedWeeks[getWeekIdentity(week, index)]
  );
  const dayCount = openWeeks.reduce(
    (sum, week) => sum + ((week.days && week.days.length) || 0),
    0
  );
  return Math.min(Math.max(48 + weeks.length * 72 + dayCount * 170, 160), 1100);
};

const MonthlyWeekDetailPanel = ({
  row,
  weekDetails,
  onCloseDetail,
  api,
  node,
}) => {
  const [weeks, setWeeks] = useState([]);
  const [collapsedWeeks, setCollapsedWeeks] = useState({});
  const syncedDetailsRef = useRef(null);

  useEffect(() => {
    if (weekDetails === undefined) return;
    if (syncedDetailsRef.current === weekDetails) return;
    syncedDetailsRef.current = weekDetails;
    setWeeks((weekDetails && weekDetails.weeks) || []);
    setCollapsedWeeks({});
  }, [weekDetails]);

  const toggleWeekAccordion = (week, index) => {
    const weekId = getWeekIdentity(week, index);
    setCollapsedWeeks((prev) => {
      const next = { ...prev, [weekId]: !prev[weekId] };
      setTimeout(() => {
        if (node && api) {
          node.setRowHeight(calcWeekDetailHeight(weeks, next));
          api.onRowHeightChanged();
        }
      }, 0);
      return next;
    });
  };

  if (weekDetails === undefined) {
    return (
      <div className="monthly-dashboard-detail-cell monthly-dashboard-detail-loading">
        <Spin tip="Loading week view..." />
      </div>
    );
  }

  if (!weekDetails || weeks.length === 0) {
    return (
      <div className="monthly-dashboard-detail-cell monthly-dashboard-detail-empty">
        <span>No week view data available.</span>
        <Icon type="up" onClick={() => onCloseDetail(row)} style={{ cursor: "pointer" }} />
      </div>
    );
  }

  return (
    <div className="monthly-dashboard-detail-cell">
      {weeks.map((week, index) => {
        const weekId = getWeekIdentity(week, index);
        const isCollapsed = Boolean(collapsedWeeks[weekId]);

        return (
          <div
            key={`${(row && row.id) || "row"}-week-${weekId}`}
            className={`monthly-dashboard-week ${isCollapsed
              ? "monthly-dashboard-week-closed"
              : "monthly-dashboard-week-open"
              }${week.is_current_week ? " monthly-dashboard-week-current" : ""}`}
          >
            <div
              className="monthly-dashboard-week-header"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleWeekAccordion(week, index);
              }}
              role="button"
              tabIndex="0"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleWeekAccordion(week, index);
                }
              }}
            >
              <div>
                <strong>Week {week.week_number}</strong>
                <span>{week.label}</span>
              </div>
              <div>
                <strong>
                  Appointments Scheduled %:{" "}
                  {formatConversionRate(week.conversion_rate)}
                </strong>
                <Icon type={isCollapsed ? "down" : "up"} style={{ cursor: "pointer" }} />
              </div>
            </div>
            {!isCollapsed && (
              <div className="monthly-dashboard-week-body">
                {(week.days || []).map((day) => {
                  return (
                    <div
                      key={`${week.week_number}-${day.date}`}
                      className={`monthly-dashboard-day-section${day.highlight ? " monthly-dashboard-day-highlight" : ""
                        }`}
                    >
                      <h4>{formatDayHeading(day)}</h4>
                      <div className="monthly-dashboard-day-grid">
                        {getDayStatCards(day).map((stat) => {
                          return (
                            <div key={stat.key} className="monthly-dashboard-day-card">
                              <div className="monthly-dashboard-kicker">{stat.label}</div>
                              <strong>
                                <Icon type={stat.icon} />
                                {stat.value}
                              </strong>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const mapInsightCards = (stats = {}, runningWeekExpectedPercentage) => {
  const expectedPercentage =
    (stats.agents_under_expected &&
      stats.agents_under_expected.expected_percentage) != null
      ? stats.agents_under_expected.expected_percentage
      : (stats.appointment_goal && stats.appointment_goal.expected_percentage) !=
        null
        ? stats.appointment_goal.expected_percentage
        : runningWeekExpectedPercentage != null
          ? runningWeekExpectedPercentage
          : 0;

  return [
    {
      key: "days_left",
      label: "Days Left",
      value: formatStatValue(stats.days_left),
      icon: "calendar",
      tone: "yellow",
      clickable: false,
    },
    {
      key: "appointment_goal",
      label: "Appointments % Goal",
      value: formatConversionRate(expectedPercentage),
      icon: "bar-chart",
      tone: "blue",
      clickable: true,
      listType: "agent",
      expectedPercentage,
    },
    {
      key: "agents_under",
      label: `Agents Under ${formatConversionRate(expectedPercentage)}`,
      value: formatStatValue(
        stats.agents_under_expected && stats.agents_under_expected.count
      ),
      icon: "line-chart",
      tone: "orange",
      clickable: true,
      listType: "agent",
      expectedPercentage,
    },
    {
      key: "agents_zero",
      label: "Agents With 0%",
      value: formatStatValue(stats.agents_with_zero_percent),
      icon: "exclamation-circle",
      tone: "pink",
      clickable: true,
      listType: "agent",
      expectedPercentage,
    },
  ];
};

const mapSummaryItems = (performanceSummary = {}) => {
  const totalCalls = performanceSummary.total_calls || {};
  const dueCalls = performanceSummary.due_calls || {};
  const agentsAtTen = performanceSummary.agents_at_10_percent_or_higher || {};

  return [
    {
      key: "assigned_agents",
      label: "Agents with B Files",
      value: formatStatValue(performanceSummary.assigned_agents),
      clickable: true,
      listType: "agent",
    },
    {
      key: "total_calls",
      label: "Total Calls",
      value: formatStatValue(totalCalls.total),
      info: true,
      tooltip: (
        <div>
          <div>Due Calls: {formatStatValue(totalCalls.due_calls)}</div>
          <div>Active 1: {formatStatValue(totalCalls.active_1)}</div>
          <div>Active 2: {formatStatValue(totalCalls.active_2)}</div>
        </div>
      ),
      clickable: true,
      listType: "bfile",
      tabCounts: {
        due_calls: totalCalls.due_calls || 0,
        active_1: totalCalls.active_1 || 0,
        active_2: totalCalls.active_2 || 0,
      },
    },
    {
      key: "unassigned_agents",
      label: "Unassigned Agents",
      value: formatStatValue(performanceSummary.non_assigned_agents),
      clickable: true,
      listType: "agent",
    },
    {
      key: "due_calls",
      label: "Unassigned Due Calls",
      value: formatStatValue(
        dueCalls.total != null
          ? dueCalls.total
          : totalCalls.due_calls
      ),
      clickable: true,
      listType: "bfile",
      info: true,
      tooltip: (
        <div>
          <div>Due Calls: {formatStatValue(
            dueCalls.count != null
              ? dueCalls.count
              : dueCalls.total != null
                ? dueCalls.total
                : totalCalls.due_calls
          )}</div>
        </div>
      ),
    },

    {
      key: "agents_ten_plus",
      label: "Agents at 10% or Higher",
      value: formatStatValue(agentsAtTen.count),
      info: true,
      tooltip:
        agentsAtTen.tooltip ||
        "Number of agents whose cumulative scheduled appointment percentage is at least 10%.",
      clickable: true,
      listType: "agent",
    },
    {
      key: "appointments_scheduled",
      label: "Appointments Scheduled",
      value: formatStatValue(performanceSummary.appointments_scheduled),
      clickable: true,
      listType: "bfile",
    },
    {
      key: "callback_requested",
      label: "Call Back Requested",
      value: formatStatValue(performanceSummary.callback_requested),
      clickable: true,
      listType: "bfile",
    },
    {
      key: "requested_reschedule",
      label: "Requested Reschedule",
      value: formatStatValue(performanceSummary.requested_reschedule),
      clickable: true,
      listType: "bfile",
    },
  ];
};

const getDayStatCards = (day = {}) => [
  {
    key: "oldest_call",
    label: "Oldest Call",
    icon: "calendar",
    value: formatWeekViewTimestamp(day.oldest_call),
  },
  {
    key: "last_call",
    label: "Last Call",
    icon: "history",
    value: formatWeekViewTimestamp(day.last_call),
  },
  {
    key: "calls_made",
    label: "Calls Made",
    icon: "phone",
    value: String(day.calls_made != null ? day.calls_made : 0),
  },
  {
    key: "appointments",
    label: "Appointments",
    icon: "calendar",
    value: String(day.appointments_scheduled != null ? day.appointments_scheduled : 0),
  },
];

const getListCount = (item = {}) => {
  if (item.review_list != null) return item.review_list;
  if (item.lead_list != null) return item.lead_list;
  if (item.opportunity_list != null) return item.opportunity_list;
  return 0;
};

const mapBoardRows = (responseData = [], monthLabel = "", page = 1) =>
  responseData.map((item, index) => {
    const conversion = Number(item.total_appointments_scheduled_percentage || 0);
    const pace = item.pace;
    const weekNumber = (page - 1) * PAGE_SIZE + index + 1;
    const highPriorityValue = item.is_high_priority;
    const isHighPriority =
      highPriorityValue === true ||
      highPriorityValue === 1 ||
      String(highPriorityValue).toLowerCase() === "high" ||
      String(highPriorityValue).toLowerCase() === "true";
    return {
      id: `${item.agency_id || "agency"}-${item.user_id || index}`,
      agency: item.agency_name || "—",
      agency_name: item.agency_name || "—",
      agency_id: item.agency_id,
      agent_name: item.agent_name || "",
      scheduler: item.scheduler_name || "—",
      scheduler_name: item.scheduler_name || "—",
      scheduler_id: item.user_id,
      week: `Week ${weekNumber}`,
      weekRange: monthLabel,
      reviewList: String(getListCount(item)),
      calls: String(item.total_calls_made || 0),
      appointments: String(item.appointments_scheduled || 0),
      conversion: `${conversion}%`,
      callbacksRequested: String(item.callbacks_requested || 0),
      pace: pace != null ? pace : "—",
      dailyNeeds: String(item.daily_needs != null ? item.daily_needs : 0),
      alert: pace === "Behind" || (conversion > 0 && conversion < 5),
      is_high_priority: isHighPriority,
      danger: isHighPriority,
      type_of_call: item.type_of_call,
      is_call_today: isEnabledToggleValue(item.is_call_today),
      raw: item,
    };
  });

const MonthlyDashboardReport = (props) => {
  const { RSFSCardFilters, setRSFSCardFilters } = useBoundStore();
  const restoredState =
    (props.location &&
      props.location.state &&
      props.location.state.fromStatList &&
      props.location.state) ||
    {};

  const [role, setRole] = useState(
    restoredState.role || RSFSCardFilters.selectedAdminToggle || REVIEWADMIN
  );
  const [toggleAdminSwitchRole, setAdminToggleSwitchRole] = useState(
    restoredState.toggleAdminSwitchRole || LEAD
  );
  const [openWeekKey, setOpenWeekKey] = useState(null);
  const [weekViewData, setWeekViewData] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(
    restoredState.selectedMonth || moment().format("M")
  );
  const [selectedCarrier, setSelectedCarrier] = useState(
    restoredState.selectedCarrier || "All Carrier"
  );
  const [selectedDateRange, setSelectedDateRange] = useState(
    restoredState.selectedDateRange !== undefined && restoredState.selectedDateRange !== 1
      ? restoredState.selectedDateRange : 4
  );
  const [boardRows, setBoardRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [insightCards, setInsightCards] = useState(() => mapInsightCards({}));
  const [summaryItems, setSummaryItems] = useState(() => mapSummaryItems({}));
  const [openSummaryTooltip, setOpenSummaryTooltip] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [expectedPercentage, setExpectedPercentage] = useState(0);

  const [searchText, setSearchText] = useState(restoredState.searchText || "");
  const [selectedAgency, setSelectedAgency] = useState(
    restoredState.selectedAgency
  );
  const [hideBadPhoneNumber, setHideBadPhoneNumber] = useState(
    restoredState.hideBadPhoneNumber !== undefined
      ? Boolean(restoredState.hideBadPhoneNumber)
      : RSFSCardFilters.hideBadPhoneNumber === undefined
        ? true
        : Boolean(RSFSCardFilters.hideBadPhoneNumber)
  );
  const [selectedScheduler, setSelectedScheduler] = useState(
    restoredState.schedulerId != null
      ? restoredState.schedulerId
      : restoredState.selectedScheduler
  );
  const [agencies, setAgencies] = useState([]);
  const [schedulers, setSchedulers] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState("agency_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortOrders, setSortOrders] = useState({ agency_name: "asc" });
  const [columnFilter, setColumnFilter] = useState({});
  // Remount grid after returning from drill-down to avoid AG Grid mid-draw conflicts.
  const [gridMountKey, setGridMountKey] = useState(0);

  const resetHandlers = useRef([]);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const filterTimeout = useRef(null);
  const gridApiRef = useRef(null);
  const fetchBoardRowsRef = useRef(null);
  const scrollYRef = useRef(0);
  const selectedMonthRef = useRef(selectedMonth);
  const hideBadPhoneNumberRef = useRef(hideBadPhoneNumber);
  const expectedPercentageRef = useRef(expectedPercentage)
  selectedMonthRef.current = selectedMonth;
  hideBadPhoneNumberRef.current = hideBadPhoneNumber;
  expectedPercentageRef.current = expectedPercentage;

  const monthOptions = Array.isArray(F.getMonths()) ? F.getMonths() : [];
  const carrierOptions = Array.isArray(F.getCarriers()) ? F.getCarriers() : [];

  const selectedMonthLabel = useMemo(() => {
    const month = monthOptions.find((m) => String(m.id) === String(selectedMonth));
    return month
      ? month.name
      : moment()
        .month(Number(selectedMonth) - 1)
        .format("MMMM");
  }, [monthOptions, selectedMonth]);

  const selectedSchedulerId = useMemo(() => {
    if (selectedScheduler == null || selectedScheduler === "") return undefined;

    const selectedSchedulerRecord = schedulers.find(
      (scheduler) =>
        String(scheduler.scheduler_id) === String(selectedScheduler) ||
        String(scheduler.scheduler_name) === String(selectedScheduler)
    );

    if (selectedSchedulerRecord) {
      return String(selectedSchedulerRecord.scheduler_id);
    }

    // Dropdown values and restored navigation state contain the scheduler ID.
    // Returning it immediately prevents an initial unfiltered request while
    // the RS/FS options are still loading.
    return /^\d+$/.test(String(selectedScheduler))
      ? String(selectedScheduler)
      : undefined;
  }, [schedulers, selectedScheduler]);

  const schedulerDropdownValue = useMemo(() => {
    if (selectedSchedulerId == null) return undefined;
    const hasMatchingOption = schedulers.some(
      (scheduler) =>
        String(scheduler.scheduler_id) === String(selectedSchedulerId)
    );
    return hasMatchingOption ? String(selectedSchedulerId) : undefined;
  }, [schedulers, selectedSchedulerId]);

  const typeOfCall =
    role === EA
      ? toggleAdminSwitchRole === LEAD
        ? PROSPECT
        : FOLLOWUP
      : REVIEW;
  const typeOfCallRef = useRef(typeOfCall);
  typeOfCallRef.current = typeOfCall;
  const schedulerHeader = role === EA ? FOLLOW_UP_SPECIALIST : REVIEW_SCHEDULER;
  const listHeader =
    role === EA
      ? toggleAdminSwitchRole === LEAD
        ? "Lead List"
        : "Opportunity List"
      : "Review List";
  const listField =
    role === EA
      ? toggleAdminSwitchRole === LEAD
        ? "lead_list"
        : "opportunity_list"
      : "review_list";

  const fetchAllRSAgencies = async () => {
    try {
      const response = await axios.get(RS_ALL_AGENCIES_API_URL);
      if (!mountedRef.current) return;
      const agencyList = (response.data || []).filter(
        (agency) => agency.test == 0
      );
      setAgencies(agencyList);
      setSelectedAgency((current) => {
        if (current == null || current === "") return current;
        const stillAvailable = agencyList.some(
          (agency) => Number(agency.id) === Number(current)
        );
        return stillAvailable ? current : undefined;
      });
    } catch (err) {
      console.error("Error fetching RS agencies:", err);
    }
  };

  const fetchAllFSAgencies = async () => {
    try {
      const response = await axios.get(GET_FS_ALL_AGENCIES);
      if (!mountedRef.current) return;
      const agencyList = (response.data || []).filter(
        (agency) => agency.test == 0
      );
      setAgencies(agencyList);
      setSelectedAgency((current) => {
        if (current == null || current === "") return current;
        const stillAvailable = agencyList.some(
          (agency) => Number(agency.id) === Number(current)
        );
        return stillAvailable ? current : undefined;
      });
    } catch (err) {
      console.error("Error fetching FS agencies:", err);
    }
  };

  const fetchAllRS = async () => {
    try {
      const response = await axios.get(GET_ACTIVE_RS);
      if (!mountedRef.current) return;
      setSchedulers((response.data && response.data.data) || []);
    } catch (err) {
      console.error("Error fetching RS:", err);
    }
  };

  const fetchAllFS = async () => {
    try {
      const response = await axios.get(GET_ACTIVE_FS);
      if (!mountedRef.current) return;
      setSchedulers((response.data && response.data.data) || []);
    } catch (err) {
      console.error("Error fetching FS:", err);
    }
  };

  useEffect(() => {
    if (
      props.location &&
      props.location.state &&
      props.location.state.fromStatList
    ) {
      setGridMountKey((key) => key + 1);
      props.history.replace({
        pathname: props.location.pathname,
        state: {},
      });
    }
  }, []);

  useEffect(() => {
    if (role === REVIEWADMIN) {
      fetchAllRS();
      fetchAllRSAgencies();
    } else {
      fetchAllFS();
      fetchAllFSAgencies();
    }
  }, [role]);

  useEffect(() => {
    // Only recalculate heights when a week accordion is open; skip empty clears
    // that race with row/column updates after navigating back from drill-down.
    if (!openWeekKey) return undefined;
    if (!(gridApiRef.current && gridApiRef.current.resetRowHeights)) {
      return undefined;
    }

    scrollYRef.current = window.scrollY;
    const timer = setTimeout(() => {
      if (gridApiRef.current && gridApiRef.current.resetRowHeights) {
        gridApiRef.current.resetRowHeights();
      }
      window.scrollTo(0, scrollYRef.current);
    }, 50);

    return () => clearTimeout(timer);
  }, [weekViewData, openWeekKey]);

  const getReportDateRange = () => {
    const today = moment();
    if (selectedDateRange === 1) {
      return { start_date: "2010-01-01", end_date: "2050-01-01" };
    }
    if (selectedDateRange === 2 || selectedDateRange === 3) {
      const date = today.subtract(selectedDateRange === 2 ? 1 : 0, "days").format("YYYY-MM-DD");
      return { start_date: date, end_date: date };
    }
    const month = selectedDateRange === 4
      ? today
      : today.startOf("month").month(Number(selectedMonth) - 1);
    return {
      start_date: month.clone().startOf("month").format("YYYY-MM-DD"),
      end_date: month.clone().endOf("month").format("YYYY-MM-DD"),
    };
  };

  const handleDateRangeChange = (value) => {
    setSelectedDateRange(value);
    if (value !== 1) {
      const date = value === 2 ? moment().subtract(1, "days") : moment();
      setSelectedMonth(date.format("M"));
    }
    setCurrentPage(1);
    setOpenWeekKey(null);
  };

  const buildCommonFilterParams = () => {
    const queryParams = new URLSearchParams({
      month: Number(selectedMonth),
      ...getReportDateRange(),
      type_of_call: typeOfCall,
      carrier: toApiCarrier(selectedCarrier),
    });

    if (selectedAgency) {
      queryParams.append("agency_id", selectedAgency);
    }

    if (hideBadPhoneNumber) {
      queryParams.append("bad_phone_number_status", false);
    }

    if (selectedSchedulerId != null) {
      queryParams.append("scheduler_ids", selectedSchedulerId);
    }

    const searchTerm = (searchText || "").trim();
    if (searchTerm) {
      queryParams.append("search", searchTerm);
    }

    return { queryParams, searchTerm };
  };

  const fetchBoardStats = async () => {
    if (!mountedRef.current || !selectedMonth || !selectedCarrier) return;

    try {
      setStatsLoading(true);
      const { queryParams } = buildCommonFilterParams();
      queryParams.delete("scheduler_ids");
      if (selectedSchedulerId != null) {
        queryParams.set("scheduler_id", selectedSchedulerId);
      }

      const response = await axios.get(
        `${WEEKLY_DASH_SCHEDULER_BOARD_STATS}?${queryParams.toString()}`
      );
      if (!mountedRef.current) return;
      const data = response.data || {};

      const mappedCards = mapInsightCards(
        data.stats || {},
        data.running_week_expected_percentage
      );
      const goalCard = mappedCards.find(
        (card) => card.key === "appointment_goal"
      );
      setExpectedPercentage(
        goalCard && goalCard.expectedPercentage != null
          ? goalCard.expectedPercentage
          : data.running_week_expected_percentage != null
            ? data.running_week_expected_percentage
            : 0
      );
      setInsightCards(mappedCards);
      setSummaryItems(mapSummaryItems(data.performance_summary || {}));
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("Error fetching scheduler board stats:", error);
      setInsightCards(mapInsightCards({}));
      setSummaryItems(mapSummaryItems({}));
      setExpectedPercentage(0);
    } finally {
      if (mountedRef.current) setStatsLoading(false);
    }
  };

  const fetchBoardRows = async () => {
    if (!mountedRef.current || !selectedMonth || !selectedCarrier) return;

    try {
      setLoading(true);
      const { queryParams, searchTerm } = buildCommonFilterParams();

      const body = {
        page: currentPage,
        per_page: PAGE_SIZE,
        sort: `${sortField || "agency_name"}:${sortDirection || "asc"}`,
      };

      if (searchTerm) {
        body.search = searchTerm;
      }
      if (selectedAgency) {
        body.agency_ids = [selectedAgency];
      }
      if (Object.keys(columnFilter).length > 0) {
        body.filter_by_column_name = columnFilter;
      }

      const response = await axios.post(
        `${WEEKLY_DASH_SCHEDULER_BOARD_GRID}?${queryParams.toString()}`,
        body
      );
      if (!mountedRef.current) return;

      const responseData = (response.data && response.data.data) || [];
      unstable_batchedUpdates(() => {
        setBoardRows(mapBoardRows(responseData, selectedMonthLabel, currentPage));
        setTotalPages(response.data.total_pages || 0);
        setOpenWeekKey(null);
        setWeekViewData({});
        setLoading(false);
      });
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("Error fetching scheduler board:", error);
      unstable_batchedUpdates(() => {
        setBoardRows([]);
        setTotalPages(0);
        setLoading(false);
      });
    }
  };
  fetchBoardRowsRef.current = fetchBoardRows;

  useEffect(() => {
    const delay = setTimeout(
      () => {
        fetchBoardStats();
      },
      searchText ? 400 : 0
    );
    return () => clearTimeout(delay);
  }, [
    selectedDateRange,
    selectedMonth,
    selectedCarrier,
    typeOfCall,
    searchText,
    selectedAgency,
    hideBadPhoneNumber,
    selectedScheduler,
    selectedSchedulerId,
  ]);

  useEffect(() => {
    const delay = setTimeout(
      () => {
        fetchBoardRows();
      },
      searchText ? 400 : 0
    );
    return () => clearTimeout(delay);
  }, [
    selectedDateRange,
    selectedMonth,
    selectedCarrier,
    typeOfCall,
    currentPage,
    sortField,
    sortDirection,
    searchText,
    selectedAgency,
    hideBadPhoneNumber,
    selectedScheduler,
    selectedSchedulerId,
    columnFilter,
  ]);

  const handleSort = (field) => {
    setSortField((prevField) => {
      const newDirection =
        prevField === field && sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      setSortOrders({ [field]: newDirection });
      return field;
    });
    setCurrentPage(1);
  };

  const handleFilter = (field, value, inputType = "text") => {
    setCurrentPage(1);

    if (field === "agency_name") {
      setSelectedAgency(value || undefined);
      return;
    }

    if (field === "scheduler_name") {
      clearTimeout(filterTimeout.current);
      filterTimeout.current = setTimeout(() => {
        setSearchText(value || "");
        setSelectedScheduler(undefined);
      }, 300);
      return;
    }

    if (inputType === "text") {
      clearTimeout(filterTimeout.current);
      filterTimeout.current = setTimeout(() => {
        setColumnFilter(value ? { [field]: value } : {});
      }, 300);
    } else {
      setColumnFilter(value ? { [field]: value } : {});
    }
  };

  const handleFilterResetHandler = (resetFn) => {
    resetHandlers.current.push(resetFn);
  };

  const handleBackToDashboard = () => {
    props.history.push("/dashboard");
  };

  const handleStatClick = (stat) => {
    if (!stat || !stat.clickable || !stat.key) return;

    const effectiveSearch = (searchText || "").trim();
    const payload = {
      role,
      toggleAdminSwitchRole,
      selectedDateRange,
      ...getReportDateRange(),
      selectedMonth,
      selectedCarrier,
      selectedAgency,
      hideBadPhoneNumber,
      searchText: effectiveSearch,
      selectedScheduler,
      schedulerId: selectedSchedulerId,
      statKey: stat.key,
      statTitle: stat.label,
      listType: stat.listType || "agent",
      tabCounts: stat.tabCounts,
      expectedPercentage:
        stat.expectedPercentage != null
          ? stat.expectedPercentage
          : expectedPercentage,
    };

    localStorage.setItem("weeklyDashboardStatCard", JSON.stringify(payload));
    props.history.push({
      pathname:
        stat.listType === "bfile"
          ? "/dashboard/monthly-report/bfile-list"
          : "/dashboard/monthly-report/stat-list",
      state: payload,
    });
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setAdminToggleSwitchRole(LEAD);
    setSearchText("");
    setSelectedAgency(undefined);
    setSelectedScheduler(undefined);
    setColumnFilter({});
    setCurrentPage(1);
    setOpenWeekKey(null);
  };

  const handleRoleSwitchChange = (fsRole) => {
    setAdminToggleSwitchRole(fsRole);
    setColumnFilter({});
    setCurrentPage(1);
    setOpenWeekKey(null);
  };

  const refresh = () => {
    resetHandlers.current.forEach((reset) => reset());
    resetHandlers.current = [];

    setSelectedDateRange(4);
    setSelectedMonth(moment().format("M"));
    setSelectedCarrier("All Carrier");
    setSearchText("");
    setSelectedAgency(undefined);
    setSelectedScheduler(undefined);
    setColumnFilter({});
    setAdminToggleSwitchRole(LEAD);
    setSortField("agency_name");
    setSortDirection("asc");
    setSortOrders({ agency_name: "asc" });
    setCurrentPage(1);
    setOpenWeekKey(null);
  };

  const toggleWeek = (weekKey) => {
    setOpenWeekKey((currentWeekKey) =>
      currentWeekKey === weekKey ? null : weekKey
    );
  };

  const fetchWeekView = async (row) => {
    if (!row || !row.agency_id || !row.scheduler_id) return;

    try {
      // const threshold =
      //   (row.raw && row.raw.appointments_scheduled != null
      //     ? row.raw.appointments_scheduled
      //     : row.appointments) || 0;

      const threshold = expectedPercentageRef.current != null ?
        expectedPercentageRef.current : 0;


      const queryParams = new URLSearchParams({
        month: Number(selectedMonthRef.current),
        type_of_call: typeOfCallRef.current,
        user_id: row.scheduler_id,
        agency_id: row.agency_id,
        threshold,
      });

      if (hideBadPhoneNumberRef.current) {
        queryParams.append("bad_phone_number_status", false);
      }

      const response = await axios.get(
        `${WEEKLY_DASH_SCHEDULER_WEEK_VIEW}?${queryParams.toString()}`
      );
      if (!mountedRef.current) return;

      scrollYRef.current = window.scrollY;
      setWeekViewData((prev) => ({
        ...prev,
        [row.id]: response.data || {},
      }));
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("Error fetching week view:", error);
      scrollYRef.current = window.scrollY;
      setWeekViewData((prev) => ({
        ...prev,
        [row.id]: null,
      }));
    }
  };

  const handleWeekToggle = (row) => {
    if (!row || !row.week) return;

    const isOpening = openWeekKey !== row.week;
    scrollYRef.current = window.scrollY;
    toggleWeek(row.week);

    if (isOpening) {
      fetchWeekView(row);
    }
  };

  const handleSchedulerPriorityToggle = async (row) => {
    if (!row || !row.agency_id || !row.scheduler_id) return;

    const previousValue = Boolean(row.is_high_priority);
    const nextValue = !previousValue;
    setBoardRows((prev) =>
      prev.map((item) =>
        item.id === row.id
          ? {
            ...item,
            is_high_priority: nextValue,
            danger: nextValue,
            priority_loading: true,
          }
          : item
      )
    );

    try {
      await axios.post(WEEKLY_DASH_SCHEDULER_DAY_PRIORITY, {
        user_id: row.scheduler_id,
        agency_id: row.agency_id,
        type_of_call: typeOfCallRef.current,
        priority_level: nextValue ? "HIGH" : "STD",
      });
      if (!mountedRef.current) return;
      message.success(`Priority Level set to ${nextValue ? "HIGH" : "STD"}`);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("Failed to update priority level:", error);
      setBoardRows((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? {
              ...item,
              is_high_priority: previousValue,
              danger: previousValue,
              priority_loading: false,
            }
            : item
        )
      );
      message.error("Failed to update Priority Level");
    } finally {
      if (mountedRef.current) setBoardRows((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, priority_loading: false } : item
        )
      );
    }
  };

  const handleSchedulerCallsTodayToggle = async (row) => {
    if (!row || !row.agency_id || !row.scheduler_id) return;

    const previousValue = Boolean(row.is_call_today);
    const nextValue = !previousValue;
    setBoardRows((prev) =>
      prev.map((item) =>
        item.id === row.id
          ? { ...item, is_call_today: nextValue, calls_today_loading: true }
          : item
      )
    );

    try {
      await axios.post(SCHEDULER_DAILY_AGENCY_TOGGLE_ASSIGNMENT, {
        user_id: row.scheduler_id,
        agency_id: row.agency_id,
        // date: moment().format("YYYY-MM-DD"),
        type_of_call: typeOfCallRef.current,
        call_today: nextValue,
      });
      if (!mountedRef.current) return;
      message.success(`Calls Today set to ${nextValue ? "YES" : "NO"}`);
      await fetchBoardRowsRef.current();
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("Failed to update calls today:", error);
      setBoardRows((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? {
              ...item,
              is_call_today: previousValue,
              calls_today_loading: false,
            }
            : item
        )
      );
      message.error("Failed to update Calls Today");
    } finally {
      if (mountedRef.current) setBoardRows((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, calls_today_loading: false } : item
        )
      );
    }
  };

  const AgencyCellRenderer = (params) => {
    if (!params.data || params.data.isDetail) return null;

    const agencyName = params.value || "—";
    const agentName = params.data.agent_name;

    if (!agentName) {
      return <span className="monthly-dashboard-agency-cell">{agencyName}</span>;
    }

    return (
      <Tooltip title={agentName} getPopupContainer={() => document.body}>
        <span className="monthly-dashboard-agency-cell">{agencyName}</span>
      </Tooltip>
    );
  };

  const WeekViewRenderer = (params) => {
    if (!params.data) return null;

    if (params.data.danger) {
      return (
        <button
          type="button"
          className="monthly-dashboard-week-arrow"
          aria-label={`Open ${params.data.week} view`}
          onClick={() => handleWeekToggle(params.data)}
        >
          <Icon type="warning" className="monthly-dashboard-warning" />
        </button>
      );
    }

    return (
      <button
        type="button"
        className="monthly-dashboard-week-arrow"
        aria-label={`Open ${params.data.week} view`}
        onClick={() => handleWeekToggle(params.data)}
      >
        <img src={arrowIcon} alt="" />
      </button>
    );
  };

  const SchedulerPriorityRenderer = (params) => {
    if (!params.data || params.data.isDetail) return null;

    const row = params.data;
    const isHighPriority = Boolean(row.is_high_priority);
    const isCallToday = Boolean(!row.is_call_today);
    const isLoading = Boolean(row.priority_loading);
    const isDisabled = isLoading || isCallToday;

    return (
      <button
        type="button"
        className={`monthly-dashboard-toggle-stat monthly-dashboard-toggle-stat-priority monthly-dashboard-scheduler-priority${isDisabled
          ? " monthly-dashboard-toggle-stat-disabled"
          : ""
          }`}
        disabled={isDisabled}
        aria-label="Toggle Priority Level"
        onClick={(e) => {
          e.stopPropagation();
          handleSchedulerPriorityToggle(row);
        }}
      >
        <span>STD</span>
        <span className={!isHighPriority ? "active-left" : "toggle-on"}>
          <i />
        </span>
        <span>HIGH</span>
      </button>
    );
  };

  const SchedulerCallsTodayRenderer = (params) => {
    if (!params.data || params.data.isDetail) return null;

    const row = params.data;
    const callsToday = Boolean(row.is_call_today);
    const isLoading = Boolean(row.calls_today_loading);

    return (
      <button
        type="button"
        className={`monthly-dashboard-toggle-stat monthly-dashboard-scheduler-priority monthly-dashboard-scheduler-calls-today${isLoading
          ? " monthly-dashboard-toggle-stat-disabled"
          : ""
          }`}
        disabled={isLoading}
        aria-label="Toggle Calls Today"
        onClick={(e) => {
          e.stopPropagation();
          handleSchedulerCallsTodayToggle(row);
        }}
      >
        <span>NO</span>
        <span className={!callsToday ? "active-left" : "toggle-on"}>
          <i />
        </span>
        <span>YES</span>
      </button>
    );
  };

  const WeekDetailRenderer = (params) => (
    <MonthlyWeekDetailPanel
      row={params.data && params.data.parent}
      weekDetails={params.data ? params.data.weekDetails : undefined}
      onCloseDetail={handleWeekToggle}
      api={params.api}
      node={params.node}
    />
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Agency",
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Agency"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "agency_name",
          sortOrder: sortOrders.agency_name,
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "select",
          options: agencies,
          handleFilterResetHandler,
        }),
        field: "agency",
        minWidth: 150,
        sortable: true,
        filter: true,
        cellRenderer: "agencyCellRenderer",
        cellClass: (params) =>
          params.data && params.data.danger ? "monthly-dashboard-danger-text" : "",
      },
      {
        headerName: schedulerHeader,
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: [schedulerHeader],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "scheduler_name",
          sortOrder: sortOrders.scheduler_name,
          handleSort,
          handleFilter,
          placeholder: TYPE_TO_FILTER_BY_NAME,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "scheduler",
        minWidth: 180,
        sortable: true,
        filter: true,
      },
      {
        headerName: listHeader,
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: [listHeader],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: listField,
          sortOrder: sortOrders[listField],
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "reviewList",
        minWidth: 130,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Total Calls Made",
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Total Calls Made"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "total_calls_made",
          sortOrder: sortOrders.total_calls_made,
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "calls",
        minWidth: 160,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Appointments Scheduled",
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Appointments Scheduled"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "appointments_scheduled",
          sortOrder: sortOrders.appointments_scheduled,
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "appointments",
        minWidth: 240,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Appointments Scheduled %",
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Appointments Scheduled %"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "total_appointments_scheduled_percentage",
          sortOrder: sortOrders.total_appointments_scheduled_percentage,
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "conversion",
        minWidth: 250,
        sortable: true,
        filter: true,
        cellClass: (params) =>
          params.data && params.data.danger ? "monthly-dashboard-danger-text" : "",
      },
      {
        headerName: "Callbacks Requested",
        headerClass: "custom-ag-header",
        headerComponent: "CustomHeader",
        headerComponentParams: (params) => ({
          headers: ["Callbacks Requested"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "callbacks_requested",
          sortOrder: sortOrders.callbacks_requested,
          handleSort,
          handleFilter,
          showPopOver: true,
          inputType: "text",
          handleFilterResetHandler,
        }),
        field: "callbacksRequested",
        minWidth: 220,
        sortable: true,
        filter: true,
      },
      // {
      //   headerClass: "custom-ag-header",
      //   headerComponent: "CustomHeader",
      //   headerComponentParams: (params) => ({
      //     headers: [
      //       <span key="pace-header" className="monthly-dashboard-header-with-info">
      //         Pace{" "}
      //         <Tooltip
      //           title="{[(Due calls x 3) + (Active 1 x 2) + (Active 2)] / Days Left} - 180"
      //           getPopupContainer={() => document.body}
      //         >
      //           <Icon
      //             type="info-circle"
      //             onClick={(e) => e.stopPropagation()}
      //             onMouseDown={(e) => e.stopPropagation()}
      //           />
      //         </Tooltip>
      //       </span>,
      //     ],
      //     colDef: params.column.getColDef(),
      //     columnApi: params.columnApi,
      //     field: "pace",
      //     sortOrder: sortOrders.pace,
      //     handleSort,
      //     handleFilter: (field, value) => handleFilter(field, value, "select"),
      //     showPopOver: true,
      //     inputType: "select",
      //     options: PACE_OPTIONS,
      //     handleFilterResetHandler,
      //   }),
      //   field: "pace",
      //   minWidth: 120,
      //   sortable: true,
      //   filter: true,
      //   cellClass: (params) => {
      //     if (!params.data) return "";
      //     if (params.data.pace === "On Track") {
      //       return "monthly-dashboard-success-text";
      //     }
      //     if (params.data.pace === "Behind" || params.data.alert || params.data.danger) {
      //       return "monthly-dashboard-danger-text";
      //     }
      //     return "";
      //   },
      // },
      // {
      //   headerClass: "custom-ag-header",
      //   headerComponent: "CustomHeader",
      //   headerComponentParams: (params) => ({
      //     headers: [
      //       <span key="daily-needs-header" className="monthly-dashboard-header-with-info">
      //         Daily Needs{" "}
      //         <Tooltip
      //           title="{(Due calls x 3) + (Active 1 x 2) + (Active 2)} / Days Left"
      //           getPopupContainer={() => document.body}
      //         >
      //           <Icon
      //             type="info-circle"
      //             onClick={(e) => e.stopPropagation()}
      //             onMouseDown={(e) => e.stopPropagation()}
      //           />
      //         </Tooltip>
      //       </span>,
      //     ],
      //     colDef: params.column.getColDef(),
      //     columnApi: params.columnApi,
      //     field: "daily_needs",
      //     sortOrder: sortOrders.daily_needs,
      //     handleSort,
      //     handleFilter,
      //     showPopOver: true,
      //     inputType: "text",
      //     handleFilterResetHandler,
      //   }),
      //   field: "dailyNeeds",
      //   minWidth: 140,
      //   sortable: true,
      //   filter: true,
      // },
      {
        headerName: "Calls Today",
        field: "is_call_today",
        minWidth: 150,
        cellRenderer: "schedulerCallsTodayRenderer",
        sortable: false,
        filter: false,
      },
      {
        headerName: "Priority",
        field: "is_high_priority",
        minWidth: 150,
        cellRenderer: "schedulerPriorityRenderer",
        sortable: false,
        filter: false,
      },
      {
        headerName: "Week View",
        field: "week",
        minWidth: 130,
        cellRenderer: "weekViewRenderer",
        sortable: false,
        filter: false,
      },
    ],
    [sortOrders, agencies, schedulerHeader, listHeader, listField]
  );

  const gridRows = useMemo(
    () =>
      boardRows.reduce((rows, row) => {
        rows.push(row);

        if (row.week === openWeekKey) {
          rows.push({
            id: `${row.id || row.week}-detail`,
            isDetail: true,
            parent: row,
            weekDetails: weekViewData[row.id],
          });
        }

        return rows;
      }, []),
    [boardRows, openWeekKey, weekViewData]
  );

  useLayoutEffect(() => {
    if (scrollYRef.current > 0) {
      window.scrollTo(0, scrollYRef.current);
    }
  }, [gridRows]);

  useEffect(() => {
    if (scrollYRef.current <= 0) return undefined;
    const timer = setTimeout(() => {
      window.scrollTo(0, scrollYRef.current);
    }, 0);
    return () => clearTimeout(timer);
  }, [gridRows]);

  return (
    <div className="monthly-dashboard-page">
      <div className="monthly-dashboard-title">Dashboard</div>
      <div
        role="button"
        tabIndex="0"
        className="monthly-dashboard-back"
        onClick={handleBackToDashboard}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBackToDashboard();
          }
        }}
      >
        <Icon type="arrow-left" />
        Back to Dashboard
      </div>

      <CommonHeader
        user={(props.auth && props.auth.user) || props.user || {}}
        onRoleChange={handleRoleChange}
        isHidden={false}
      />

      <div className="monthly-dashboard-toolbar">
        <div className="monthly-dashboard-selects">
          <Select
            allowClear
            showSearch
            placeholder="Select Month"
            value={selectedMonth || undefined}
            onChange={(value) => {
              const month = value || moment().format("M");
              setSelectedMonth(month);
              setSelectedDateRange(Number(month) === moment().month() + 1 ? 4 : null);
              setOpenWeekKey(null);
              setCurrentPage(1);
            }}
            className="monthly-dashboard-select"
          >
            {monthOptions.map((opt) =>
              opt ? (
                <Option key={opt.id} value={opt.id}>
                  <span style={{ fontSize: "small" }}>{opt.name}</span>
                </Option>
              ) : null
            )}
          </Select>
          <Select
            allowClear
            showSearch
            placeholder="Select Carrier"
            value={selectedCarrier || undefined}
            onChange={(value) => {
              setSelectedCarrier(value || "All Carrier");
              setCurrentPage(1);
            }}
            className="monthly-dashboard-select"
          >
            {carrierOptions.map((opt) =>
              opt ? (
                <Option key={opt.id} value={opt.id}>
                  <span style={{ fontSize: "small" }}>{opt.name}</span>
                </Option>
              ) : null
            )}
          </Select>
        </div>

        <div className="monthly-dashboard-actions">
          <div
          // style={{marginRight: "220px"}}
          >
            <label className="monthly-dashboard-bad-phone-label">
              <input
                type="checkbox"
                checked={hideBadPhoneNumber}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setHideBadPhoneNumber(checked);
                  setRSFSCardFilters({ hideBadPhoneNumber: checked ? 1 : 0 });
                  setCurrentPage(1);
                }}
              />
              <span>Hide Bad Phone Number</span>
            </label>
          </div>
          <div>
            <Select
              className="monthly-dashboard-agency-select"
              placeholder="Search by Agency"
              allowClear
              showSearch
              value={selectedAgency}
              optionFilterProp="children"
              dropdownClassName="monthly-dashboard-filter-dropdown"
              onChange={(value) => {
                setSelectedAgency(value || undefined);
                setCurrentPage(1);
              }}
            >
              {agencies.map((agency) => (
                <Option key={agency.id} value={agency.id}>
                  {agency.name}
                </Option>
              ))}
            </Select>
            {role === EA && (
              <Radio.Group
                value={toggleAdminSwitchRole}
                onChange={(e) => handleRoleSwitchChange(e.target.value)}
                className="monthly-dashboard-lead-toggle"
              >
                <Radio.Button
                  value={LEAD}
                  className={
                    toggleAdminSwitchRole === LEAD
                      ? "review-btn-active"
                      : "review-btn-inactive"
                  }
                  style={{
                    width: 120,
                    textAlign: "center",
                    borderRadius: "20px 0px 0px 20px",
                  }}
                >
                  {LEAD}
                </Radio.Button>
                <Radio.Button
                  value={OPPORTUNITY}
                  className={
                    toggleAdminSwitchRole === OPPORTUNITY
                      ? "ea-btn-active"
                      : "ea-btn-inactive"
                  }
                  style={{
                    width: 120,
                    textAlign: "center",
                    borderRadius: "0px 20px 20px 0px",
                  }}
                >
                  {OPPORTUNITY}
                </Radio.Button>
              </Radio.Group>
            )}
            {/* <Button type="primary" className="monthly-dashboard-export">
            <Icon type="upload" />
            Export Data
          </Button> */}
            <Button
              type="primary"
              className="monthly-dashboard-refresh"
              loading={loading}
              onClick={refresh}
            >
              <Icon type="reload" />
            </Button>
          </div>
        </div>
      </div>

      <div className="monthly-dashboard-filter-row">
        <h3>
          {selectedCarrier} {selectedCarrier && selectedMonth && <span> - </span>}{" "}
          <span>
            {selectedMonth && (
              <Translate
                text={moment()
                  .month(Number(selectedMonth) - 1)
                  .format("MMMM")}
              />
            )}
          </span>
        </h3>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div className="radio-btns-container">
            <Radio.Group
              value={selectedDateRange}
              onChange={(event) => handleDateRangeChange(event.target.value)}
            >
              {/* <Radio value={1} className="font-size-13">All</Radio> */}
              <Radio value={2} className="font-size-13">Yesterday</Radio>
              <Radio value={3} className="font-size-13">Today</Radio>
              <Radio value={4} className="font-size-13">This Month</Radio>
            </Radio.Group>
          </div>
          <Select
            className="monthly-dashboard-board-scheduler-filter"
            placeholder={
              role === EA
                ? "Search by Follow-Up Specialist"
                : "Search by Review Scheduler"
            }
            allowClear
            showSearch
            value={schedulerDropdownValue}
            optionFilterProp="children"
            dropdownClassName="monthly-dashboard-filter-dropdown"
            onChange={(value) => {
              setSelectedScheduler(value || undefined);
              setSearchText("");
              setCurrentPage(1);
            }}
          >
            {schedulers.map((scheduler) => (
              <Option
                key={String(scheduler.scheduler_id)}
                value={String(scheduler.scheduler_id)}
              >
                {scheduler.scheduler_name}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div
        className="monthly-dashboard-insights"
        style={{ opacity: statsLoading ? 0.6 : 1 }}
      >
        {insightCards.map((card) => (
          <div
            key={card.key || card.label}
            role={card.clickable ? "button" : undefined}
            tabIndex={card.clickable ? 0 : undefined}
            className={`monthly-dashboard-insight monthly-dashboard-insight-${card.tone}${card.clickable ? " monthly-dashboard-stat-clickable" : ""
              }`}
            onClick={() => handleStatClick(card)}
            onKeyDown={(e) => {
              if (
                card.clickable &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                handleStatClick(card);
              }
            }}
          >
            <div>
              <div className="monthly-dashboard-kicker">{card.label}</div>
              <strong>{card.value}</strong>
            </div>
            <span>
              <Icon type={card.icon} />
            </span>
          </div>
        ))}
      </div>

      <section className="monthly-dashboard-summary">
        <h3>
          <Icon type="sync" />
          Performance Summary
        </h3>
        <div
          className="monthly-dashboard-summary-grid"
          style={{ opacity: statsLoading ? 0.6 : 1 }}
        >
          {summaryItems.map((item) => (
            <div
              key={item.key || item.label}
              role={item.clickable ? "button" : undefined}
              tabIndex={item.clickable ? 0 : undefined}
              className={
                item.clickable ? "monthly-dashboard-stat-clickable" : undefined
              }
              onClick={() => handleStatClick(item)}
              onKeyDown={(e) => {
                if (
                  item.clickable &&
                  (e.key === "Enter" || e.key === " ")
                ) {
                  e.preventDefault();
                  handleStatClick(item);
                }
              }}
            >
              <div>
                {item.label}
                {item.info && (
                  <Tooltip
                    title={item.tooltip}
                    visible={openSummaryTooltip === item.key}
                    onVisibleChange={(visible) => {
                      setOpenSummaryTooltip((current) =>
                        visible ? item.key : current === item.key ? null : current
                      );
                    }}
                    placement="bottom"
                    overlayStyle={{ zIndex: 1100 }}
                    transitionName=""
                    trigger={["hover", "click"]}
                    getPopupContainer={() => document.body}
                  >
                    <button
                      type="button"
                      aria-label={`Information about ${item.label}`}
                      aria-expanded={openSummaryTooltip === item.key}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                        if (event.key === "Escape") setOpenSummaryTooltip(null);
                      }}
                      style={{
                        background: "transparent",
                        border: 0,
                        padding: "2px 4px",
                        cursor: "pointer",
                      }}
                    >
                      <Icon type="info-circle" />
                    </button>
                  </Tooltip>
                )}
              </div>
              <strong>{item.value}</strong>
              <span />
            </div>
          ))}
        </div>
      </section>

      <section className="monthly-dashboard-board">
        <div className="monthly-dashboard-board-title">
          <h3>
            <Icon type="table" />
            Scheduler Board
          </h3>
          <div className="monthly-dashboard-board-title-actions">
            {/* <Input
              className="monthly-dashboard-board-search"
              placeholder="Search"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setSelectedScheduler(undefined);
                setCurrentPage(1);
              }}
              suffix={<Icon type="search" />}
            /> */}
            <span>
              {selectedMonth && (
                <Translate
                  text={moment()
                    .month(Number(selectedMonth) - 1)
                    .format("MMMM")}
                />
              )}{" "}
              Efficiency
            </span>
          </div>
        </div>

        <div className="monthly-dashboard-aggrid" style={{ position: "relative" }}>
          <CustomAgGridTable
            key={gridMountKey}
            rowDatas={gridRows}
            columnDefss={columnDefs}
            onGridReady={(params) => {
              gridApiRef.current = params.api;
            }}
            frameworkComponents={{
              CustomHeader,
              agencyCellRenderer: AgencyCellRenderer,
              schedulerCallsTodayRenderer: SchedulerCallsTodayRenderer,
              schedulerPriorityRenderer: SchedulerPriorityRenderer,
              weekViewRenderer: WeekViewRenderer,
              weekDetailRenderer: WeekDetailRenderer,
            }}
            gridApiOptions={{
              // domLayout: "autoHeight",
              // headerHeight: 56,
              // rowHeight: 64,
              isFullWidthRow: (params) =>
                params.rowNode.data && params.rowNode.data.isDetail,
              fullWidthCellRenderer: "weekDetailRenderer",
              getRowHeight: (params) => {
                if (!(params.data && params.data.isDetail)) return 64;

                const weeks =
                  (params.data.weekDetails && params.data.weekDetails.weeks) || [];
                return calcWeekDetailHeight(weeks);
              },
              getRowClass: (params) => {
                if (params.data && params.data.isDetail) {
                  return "monthly-dashboard-accordion-row";
                }
                if (params.data && params.data.is_high_priority) {
                  return "monthly-dashboard-danger-row";
                }
                return "";
              },
            }}
          />
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255,255,255,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <Spin tip="Loading ..." />
            </div>
          )}
        </div>

        <div className="monthly-dashboard-pagination">
          <Pagination
            current={currentPage}
            total={totalPages * PAGE_SIZE}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            onChange={(page) => setCurrentPage(page)}
          />
        </div>
      </section>
    </div>
  );
};

export default MonthlyDashboardReport;



// WEBPACK FOOTER //
// ./src/scenes/Dashboard/parts/review-admin/MonthlyDashboardReport.js